import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, ExternalLink, Search, FileCode, Image, Map } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { SEO_PAGES, PROD_DOMAIN } from '@/lib/seoRegistry';

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const Check = ({ ok, warn = false }) =>
  ok ? <CheckCircle2 className="w-4 h-4 text-teal-600" /> : warn ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <XCircle className="w-4 h-4 text-red-500" />;

const StatCard = ({ icon: Icon, label, value, ok }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ok ? 'bg-teal-50 text-teal-600' : 'bg-red-50 text-red-500'}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-semibold text-slate-900">{value}</div>
    </div>
  </div>
);

export default function AdminSeoPage() {
  const [sitemapUrls, setSitemapUrls] = useState(null);
  const [robotsOk, setRobotsOk] = useState(null);
  const [ogImageOk, setOgImageOk] = useState(null);

  useEffect(() => {
    fetch('/sitemap.xml').then(r => r.text()).then(xml => {
      setSitemapUrls((xml.match(/<loc>([^<]+)<\/loc>/g) || []).map(l => l.replace(/<\/?loc>/g, '').replace(PROD_DOMAIN, '') || '/'));
    }).catch(() => setSitemapUrls([]));
    fetch('/robots.txt').then(r => r.text()).then(t => setRobotsOk(t.includes('Sitemap:'))).catch(() => setRobotsOk(false));
    fetch('/og-image.jpg', { method: 'HEAD' }).then(r => setOgImageOk(r.ok)).catch(() => setOgImageOk(false));
  }, []);

  const titles = SEO_PAGES.map(p => p.title);
  const rows = SEO_PAGES.map(p => {
    const inSitemap = sitemapUrls ? sitemapUrls.includes(p.path) : null;
    return {
      ...p,
      titleUnique: titles.filter(t => t === p.title).length === 1,
      titleLenOk: p.title.length <= 60,
      descLenOk: p.description.length > 0 && p.description.length <= 160,
      inSitemap,
      sitemapOk: p.noindex ? inSitemap === false : inSitemap === true,
    };
  });
  const pagesOk = rows.filter(r => r.titleUnique && r.descLenOk && (sitemapUrls === null || r.sitemapOk)).length;

  return (
    <div className="space-y-6" data-testid="admin-seo-page">
      <PageHeader
        title="SEO-Status"
        description="Interner SEO-Zustand aller öffentlichen Seiten – ideal als Kontrolle nach jedem Redeploy"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Search} label="Seiten OK" value={`${pagesOk} / ${SEO_PAGES.length}`} ok={pagesOk === SEO_PAGES.length} />
        <StatCard icon={Map} label="Sitemap-URLs" value={sitemapUrls === null ? '…' : sitemapUrls.length} ok={(sitemapUrls?.length || 0) >= 13} />
        <StatCard icon={FileCode} label="robots.txt → Sitemap" value={robotsOk === null ? '…' : robotsOk ? 'Verlinkt' : 'Fehlt'} ok={!!robotsOk} />
        <StatCard icon={Image} label="og-image.jpg" value={ogImageOk === null ? '…' : ogImageOk ? 'Erreichbar' : 'Fehlt'} ok={!!ogImageOk} />
      </div>

      <motion.div initial="hidden" animate="visible" variants={fadeIn} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                <th className="px-4 py-3 font-medium">Seite</th>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium">Schema</th>
                <th className="px-4 py-3 font-medium">Sitemap</th>
                <th className="px-4 py-3 font-medium text-right">Prüfen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.path} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid={`seo-row-${r.path.replace(/\//g, '') || 'home'}`}>
                  <td className="px-4 py-3">
                    <a href={r.path} target="_blank" rel="noreferrer" className="font-medium text-slate-900 hover:text-teal-600">{r.path}</a>
                    {r.noindex && <Badge variant="outline" className="ml-2 text-xs text-slate-500">noindex</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Check ok={r.titleUnique && r.titleLenOk} warn={r.titleUnique && !r.titleLenOk} />
                      <span className="text-slate-600 max-w-[280px] truncate" title={r.title}>{r.title}</span>
                      <span className={`text-xs ${r.titleLenOk ? 'text-slate-400' : 'text-amber-600'}`}>{r.title.length}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Check ok={r.descLenOk} />
                      <span className={`text-xs ${r.descLenOk ? 'text-slate-400' : 'text-red-500'}`}>{r.description.length}/160</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.faqCount > 0 && <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50 text-xs">FAQ ({r.faqCount})</Badge>}
                      {r.schemas.filter(s => s !== 'FAQPage').map(s => <Badge key={s} variant="outline" className="text-xs text-slate-500">{s}</Badge>)}
                      {r.faqCount === 0 && r.schemas.length === 0 && <span className="text-xs text-slate-400">–</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Check ok={r.sitemapOk === true} warn={r.sitemapOk === null} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <a href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(PROD_DOMAIN + r.path)}`} target="_blank" rel="noreferrer">
                        <Button variant="ghost" size="sm" className="text-teal-600 hover:text-teal-700 text-xs" data-testid={`seo-rich-test-${r.path.replace(/\//g, '') || 'home'}`}>
                          Rich Results <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      <p className="text-xs text-slate-400">
        Hinweis: Die Werte stammen aus der SEO-Registry der App (Stand des Codes). Sitemap, robots.txt und og-image werden live geprüft. Der Rich-Results-Link testet die Produktions-URL ({PROD_DOMAIN}) – dort muss der aktuelle Stand deployed sein.
      </p>
    </div>
  );
}
